'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { HeartIcon } from '@heroicons/react/24/outline';
import POHLOGO from 'assets/images/nav-poh-logo.png';
import Arrow from 'assets/icons/arrow_back.svg';
import PersonIcon from 'assets/icons/person.svg';
import POHIcon from 'assets/images/poh-icon.png';
import ExploreHearts from 'assets/images/explore-hearts.png';
import KcRegion from 'assets/images/kc-region.png';
import MyHearts from 'assets/images/my-hearts.png';
import Merch from 'assets/images/merch.png';
import About from 'assets/images/about.png';
import NewsIcon from 'assets/images/news-icon.webp';
import LoginIcon from 'assets/images/login-icon.png';
import Hamburger from 'assets/images/hamburger.png';
import KCCommunities from 'assets/images/KC_Communities.png';
import { CloseRoundIcon } from '@/components/atoms/Icons';
import LoginModal from '@/components/auth/loginModal';
import { useAuth } from '@/providers/userContext';
import { setPostLoginRedirect } from '@/lib/client/postLoginRedirect';
import { logout as logoutAction } from '@/lib/client/sessionActions';
import { getAllSaved, subscribe as subscribeCache, getAllGuestSaved } from '@/lib/client/interactionCache';
import LogoutModal from '@/components/auth/logoutModal';
import { useHeartInteractions } from '@/lib/client/useHeartInteractions';
import { decorateNav } from './header.utils';
import Style from './Header.module.css';
//import { fetchJson } from '@/lib/client/fetchJson';

/* ── Inline SVGs (decorative) ────────────────────────────────────────── */
const Icon = {
  hamburger: p => <svg viewBox="0 0 23 16" width="23" height="16" aria-hidden="true" {...p}>
      <path d="M1 1h20  M1 8h21  M9 15h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>,
  person: ({
    className,
    ...rest
  } = {}) => <Image src={PersonIcon} alt="" aria-hidden="true" width={20} height={20} className={['w-5 h-5', className].filter(Boolean).join(' ')} {...rest} />
};
const renderIcon = name => {
  switch (name) {
    case 'heart':
      return <Image src={ExploreHearts} alt="Explore Hearts icon" className="max-h-[22px] lg:max-h-9 max-w-fit lg:max-w-none w-auto" />;
    case 'region':
      return <Image src={KcRegion} alt="Region icon" className="max-h-[22px] lg:max-h-9 max-w-fit lg:max-w-none w-auto" />;
    case 'myhearts':
      return <Image src={MyHearts} alt="User icon" className="max-h-[22px] lg:max-h-8 max-w-fit lg:max-w-none" />;
    case 'merch':
      return <Image src={Merch} alt="shopping icon" className="max-h-[22px] lg:max-h-9 max-w-fit lg:max-w-none w-auto" />;
    case 'kccommunities':
      return <Image src={KCCommunities} alt="shopping icon" className="max-h-[22px] lg:max-h-9 max-w-fit lg:max-w-none w-auto" />;
    case 'about':
      return <Image src={About} alt="about" className="max-h-[22px] lg:max-h-none max-w-5.5 lg:max-w-full" />;
    case 'news':
      return <Image src={NewsIcon} alt="speaker icon" className="max-h-5 lg:max-h-8 max-w-fit lg:max-w-none" />;
    case 'lock':
      return <Image src={LoginIcon} alt="Login icon" className="max-h-[22px] lg:max-h-9 max-w-fit lg:max-w-none w-auto" />;
    default:
      return null;
  }
};

/* ── Header Component ────────────────────────────────────────────────── */
export default function Header(t0) {
  const $ = _c(128);
  const {
    nav: t1,
    dynamicPages: t2
  } = t0;
  let t3;
  if ($[0] !== t1) {
    t3 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const nav = t3;
  let t4;
  if ($[2] !== t2) {
    t4 = t2 === undefined ? [] : t2;
    $[2] = t2;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const dynamicPages = t4;
  const router = useRouter();
  const {
    user
  } = useAuth();
  const isAuthed = Boolean(user);
  const [savedHeartsCount, setSavedHeartsCount] = useState(0);
  useHeartInteractions(1);
  let t5;
  if ($[4] !== user) {
    t5 = () => {
      if (!user || !user.id) {
        const guestSaved = getAllGuestSaved();
        return guestSaved?.length || 0;
      }
      const authSaved = getAllSaved();
      return authSaved?.length || 0;
    };
    $[4] = user;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const subscribedSavedHeartsCount = useSyncExternalStore(subscribeCache, t5, _temp);
  let t6;
  let t7;
  if ($[6] !== subscribedSavedHeartsCount) {
    t6 = () => {
      setSavedHeartsCount(subscribedSavedHeartsCount);
    };
    t7 = [subscribedSavedHeartsCount];
    $[6] = subscribedSavedHeartsCount;
    $[7] = t6;
    $[8] = t7;
  } else {
    t6 = $[7];
    t7 = $[8];
  }
  useEffect(t6, t7);
  const [open, setOpen] = useState(false);
  const [show, setShow] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setShowLoginModal(true);
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const openLoginModal = t8;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => setShowLogoutModal(true);
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const openLogoutModal = t9;
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = async () => {
      ;
      try {
        await logoutAction();
        setShowLogoutModal(false);
      } catch (t11) {
        const err = t11;
        console.error("Logout failed:", err);
      }
    };
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const confirmLogout = t10;
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = href => {
      if (href) {
        setPostLoginRedirect(href);
      }
      setShowLoginModal(true);
    };
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  const openLoginWithRedirect = t11;
  let t12;
  if ($[13] !== nav) {
    t12 = decorateNav(nav, _temp2);
    $[13] = nav;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  const decoratedNav = t12;
  let t13;
  bb0: {
    if (!decoratedNav.length) {
      let t14;
      if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
        t14 = [];
        $[15] = t14;
      } else {
        t14 = $[15];
      }
      t13 = t14;
      break bb0;
    }
    let t14;
    if ($[16] !== decoratedNav || $[17] !== isAuthed) {
      const loginItem = decoratedNav.find(_temp3);
      const accountChildren = loginItem?.children ?? [];
      const accountChildLabels = new Set(accountChildren.map(_temp4));
      const accountChildHrefs = new Set(accountChildren.map(_temp5).filter(Boolean));
      const accountHrefPrefixes = ["/account/wishlist", "/account/visited", "/account/my-account"];
      t14 = decoratedNav.flatMap(item_0 => {
        if (item_0.label === "Login") {
          if (isAuthed) {
            return [{
              ...item_0,
              label: "Logout",
              href: undefined,
              children: undefined,
              isLogoutAction: true
            }];
          }
          return [{
            ...item_0,
            href: undefined,
            children: undefined,
            isLoginAction: true
          }];
        }
        if (item_0.label === "My Hearts") {
          const children = accountChildren.length ? accountChildren : item_0.children ?? [];
          const guardedChildren = children.map(child_1 => ({
            ...child_1,
            requiresAuth: !isAuthed && Boolean(child_1.href && ["/account/wishlist", "/account/visited", "/account/my-account"].some(prefix => child_1.href.startsWith(prefix)))
          }));
          return [{
            ...item_0,
            href: undefined,
            children: guardedChildren
          }];
        }
        if (isAuthed && (accountChildLabels.has(item_0.label) || accountChildHrefs.has(item_0.href) || accountHrefPrefixes.some(prefix_0 => item_0.href?.startsWith(prefix_0)))) {
          return [];
        }
        return [item_0];
      });
      $[16] = decoratedNav;
      $[17] = isAuthed;
      $[18] = t14;
    } else {
      t14 = $[18];
    }
    t13 = t14;
  }
  const authNav = t13;
  let t14;
  if ($[19] !== authNav) {
    t14 = authNav.filter(_temp6);
    $[19] = authNav;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  const desktopNav = t14;
  let t15;
  if ($[21] !== authNav) {
    t15 = authNav.find(_temp7);
    $[21] = authNav;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  const myHeartsItem = t15;
  let t16;
  if ($[23] !== isAuthed) {
    t16 = href_0 => Boolean(!isAuthed && href_0 && ["/account/wishlist", "/account/visited", "/account/my-account"].some(prefix_1 => href_0.startsWith(prefix_1)));
    $[23] = isAuthed;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  const requiresAuthForHref = t16;
  let t17;
  if ($[25] !== authNav) {
    t17 = [{
      title: "",
      items: authNav
    }];
    $[25] = authNav;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  const basePanel = t17;
  const [stack, setStack] = useState(basePanel);
  const mobileBtnRef = useRef(null);
  const desktopBtnRef = useRef(null);
  const panelRef = useRef(null);
  const navRef = useRef(null);
  const accountMenuRef = useRef(null);
  const [openMenu, setOpenMenu] = useState(-1);
  const [openAccountMenu, setOpenAccountMenu] = useState(false);
  const desktopBarRef = useRef(null);
  let t18;
  if ($[27] !== basePanel) {
    t18 = () => {
      setStack(basePanel);
      setOpen(true);
      requestAnimationFrame(() => setShow(true));
    };
    $[27] = basePanel;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  const openDrawer = t18;
  let t19;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = opts => {
      const restoreFocus = opts?.restoreFocus !== false;
      setShow(false);
      setTimeout(() => {
        setOpen(false);
        if (restoreFocus) {
          const btn = mobileBtnRef.current || desktopBtnRef.current;
          btn?.focus();
        }
      }, 250);
    };
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  const closeDrawer = t19;
  let t20;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => {
      setPostLoginRedirect(window.location.href);
      openLoginModal();
      closeDrawer();
    };
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  const openLoginFromDrawer = t20;
  let t21;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = href_1 => {
      if (href_1) {
        setPostLoginRedirect(href_1);
      }
      openLoginModal();
      closeDrawer();
    };
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  const openLoginFromDrawerWithRedirect = t21;
  let t22;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = () => {
      openLogoutModal();
      closeDrawer();
    };
    $[32] = t22;
  } else {
    t22 = $[32];
  }
  const confirmLogoutFromDrawer = t22;
  let t23;
  let t24;
  if ($[33] !== open || $[34] !== show) {
    t23 = () => {
      document.documentElement.style.overflow = open && show ? "hidden" : "";
      return _temp8;
    };
    t24 = [open, show];
    $[33] = open;
    $[34] = show;
    $[35] = t23;
    $[36] = t24;
  } else {
    t23 = $[35];
    t24 = $[36];
  }
  useEffect(t23, t24);
  let t25;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = () => {
      const list = navRef.current?.querySelectorAll("a[href],button:not([disabled]),[tabindex]:not([tabindex=\"-1\"])");
      if (list && list.length) {
        list[0].focus();
      }
    };
    $[37] = t25;
  } else {
    t25 = $[37];
  }
  const focusFirstMenuItem = t25;
  let t26;
  let t27;
  if ($[38] !== open || $[39] !== show) {
    t26 = () => {
      if (open && show) {
        requestAnimationFrame(() => focusFirstMenuItem());
      }
    };
    t27 = [open, show, focusFirstMenuItem];
    $[38] = open;
    $[39] = show;
    $[40] = t26;
    $[41] = t27;
  } else {
    t26 = $[40];
    t27 = $[41];
  }
  useEffect(t26, t27);
  let t28;
  bb1: {
    if (!open) {
      t28 = stack;
      break bb1;
    }
    let t29;
    if ($[42] !== basePanel || $[43] !== stack) {
      let t30;
      if ($[45] !== basePanel) {
        t30 = (panel, idx) => idx === 0 ? {
          ...panel,
          items: basePanel[0]?.items ?? panel.items
        } : panel;
        $[45] = basePanel;
        $[46] = t30;
      } else {
        t30 = $[46];
      }
      t29 = stack.map(t30);
      $[42] = basePanel;
      $[43] = stack;
      $[44] = t29;
    } else {
      t29 = $[44];
    }
    t28 = t29;
  }
  const stackForRender = t28;
  let t29;
  if ($[47] !== stack.length) {
    t29 = e => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (stack.length > 1) {
          setStack(_temp9);
          requestAnimationFrame(() => focusFirstMenuItem());
        } else {
          closeDrawer();
        }
        return;
      }
      if (e.key === "Tab") {
        const focusables = panelRef.current?.querySelectorAll("a[href],button:not([disabled]),[tabindex]:not([tabindex=\"-1\"])");
        const items = focusables ? Array.from(focusables) : [];
        if (!items.length) {
          return;
        }
        const first = items[0];
        const last = items[items.length - 1];
        const active = document.activeElement;
        if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        } else {
          if (e.shiftKey && active === first) {
            e.preventDefault();
            last.focus();
          }
        }
      }
    };
    $[47] = stack.length;
    $[48] = t29;
  } else {
    t29 = $[48];
  }
  const onPanelKeyDown = t29;
  let t30;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = () => setStack(_temp10);
    $[49] = t30;
  } else {
    t30 = $[49];
  }
  const popPanel = t30;
  const currentIndex = stack.length - 1;
  let t31;
  let t32;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = () => {
      const onDocClick = function onDocClick(e_0) {
        if (!desktopBarRef.current) {
          return;
        }
        if (!desktopBarRef.current.contains(e_0.target)) {
          setOpenMenu(-1);
        }
      };
      document.addEventListener("pointerdown", onDocClick);
      return () => document.removeEventListener("pointerdown", onDocClick);
    };
    t32 = [];
    $[50] = t31;
    $[51] = t32;
  } else {
    t31 = $[50];
    t32 = $[51];
  }
  useEffect(t31, t32);
  let t33;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = index => setOpenMenu(i => i === index ? -1 : index);
    $[52] = t33;
  } else {
    t33 = $[52];
  }
  const toggleMenu = t33;
  let t34;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <Image src={Hamburger} alt="menu icon" width={17} height={11} className="w-[17px]" />;
    $[53] = t34;
  } else {
    t34 = $[53];
  }
  let t35;
  if ($[54] !== open || $[55] !== openDrawer) {
    t35 = <button ref={mobileBtnRef} className="p-3 px-3 lg:-mr-2 rounded focus-visible:ring-2 focus-visible:ring-white cursor-pointer block lg:hidden -ml-2" aria-label="Open menu" aria-haspopup="dialog" aria-expanded={open} aria-controls="header-mobile-menu" onClick={openDrawer}>{t34}</button>;
    $[54] = open;
    $[55] = openDrawer;
    $[56] = t35;
  } else {
    t35 = $[56];
  }
  let t36;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <Link href="/" className="flex! lg:relative absolute left-0 right-0 mx-auto lg:w-auto w-fit top-3.5 sm:top-2.75 lg:top-0"><Image src={POHLOGO} alt="Parade of Hearts" width={245} height={39} className="block 2xl:w-[245px] xl:w-[230px] lg:w-[180px] w-[150px] h-auto" /></Link>;
    $[57] = t36;
  } else {
    t36 = $[57];
  }
  let t37;
  if ($[58] !== t35) {
    t37 = <div className="flex items-start md:items-center gap-2">{t35}{t36}</div>;
    $[58] = t35;
    $[59] = t37;
  } else {
    t37 = $[59];
  }
  let t38;
  if ($[60] !== desktopNav || $[61] !== openMenu || $[62] !== router) {
    let t39;
    if ($[64] !== openMenu || $[65] !== router) {
      t39 = (item_3, idx_0) => {
        if (item_3.showDesktopMenu && !item_3.hide) {
          if (item_3.isLoginAction) {
            return <button key={item_3.label} type="button" className={`${Style.menu} transition-opacity font-semibold  duration-300 ease-in-out 2xl:text-[17px] xl:text-base text-sm leading-[2.5em] px-1 hover:opacity-90 focus-visible:ring-2 focus-visible:ring-white`} onClick={openLoginModal}>{item_3.label}</button>;
          } else {
            if (item_3.isLogoutAction) {
              return <button key={item_3.label} type="button" className={`${Style.menu} transition-opacity font-semibold  duration-300 ease-in-out 2xl:text-[17px] xl:text-base text-sm leading-[2.5em] px-1 hover:opacity-90 focus-visible:ring-2 focus-visible:ring-white`} onClick={openLogoutModal}>{item_3.label}</button>;
            } else {
              if (item_3.children) {
                return <div key={item_3.label} className="relative px-1" onMouseEnter={() => setOpenMenu(idx_0)} onMouseLeave={() => setOpenMenu(i_0 => i_0 === idx_0 ? -1 : i_0)}><button className={`${Style.menu} ${item_3.href ? "cursor-pointer" : "cursor-default"} font-semibold  transition-opacity duration-300 ease-in-out inline-flex items-center gap-1 2xl:text-[17px] xl:text-base text-sm leading-[2.5em] hover:opacity-90 focus-visible:ring-2 focus-visible:ring-white`} aria-haspopup="menu" aria-expanded={openMenu === idx_0} onClick={() => item_3.href ? router.push(item_3.href) : toggleMenu(idx_0)} onKeyDown={e_1 => {
                    if (e_1.key === "Escape") {
                      setOpenMenu(-1);
                    }
                  }}>{item_3.label}</button>{openMenu === idx_0 && <ul role="menu" className={`${Style.subMenuWrapper} absolute left-0 mt-0 xl:w-48 w-40 rounded-xl bg-neutral-900/95 backdrop-blur border border-white/10 shadow-lg z-40`}>{item_3.children.filter(_temp11).map(child_2 => <li key={child_2.label} role="none">{child_2.requiresAuth ? <button type="button" role="menuitem" className={`${Style.subMenu} block w-full text-left px-3 py-2 text-sm cursor-pointer focus-visible:ring-2 focus-visible:ring-white`} onClick={() => openLoginWithRedirect(child_2.href)}>{child_2.label}</button> : <Link role="menuitem" href={child_2.href} className={`${Style.subMenu} block px-3 py-2 text-sm cursor-pointer focus-visible:ring-2 focus-visible:ring-white`} onClick={() => setOpenMenu(-1)}>{child_2.label}</Link>}</li>)}</ul>}</div>;
              } else {
                return <Link key={item_3.label} href={item_3.href} target={item_3.external ? "_blank" : "_self"} className={`${Style.menu} transition-opacity font-semibold  duration-300 ease-in-out 2xl:text-[17px] xl:text-base text-sm leading-[2.5em] px-1 hover:opacity-90 focus-visible:ring-2 focus-visible:ring-white`}>{item_3.label}</Link>;
              }
            }
          }
        }
      };
      $[64] = openMenu;
      $[65] = router;
      $[66] = t39;
    } else {
      t39 = $[66];
    }
    t38 = desktopNav.map(t39);
    $[60] = desktopNav;
    $[61] = openMenu;
    $[62] = router;
    $[63] = t38;
  } else {
    t38 = $[63];
  }
  let t39;
  if ($[67] !== t38) {
    t39 = <nav className={`${Style.navbar} hidden lg:flex items-center 2xl:gap-15 xl:gap-12.5 gap-10`} aria-label="Primary">{t38}</nav>;
    $[67] = t38;
    $[68] = t39;
  } else {
    t39 = $[68];
  }
  let t40;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <HeartIcon className="w-6 h-6" />;
    $[69] = t40;
  } else {
    t40 = $[69];
  }
  let t41;
  if ($[70] !== savedHeartsCount) {
    t41 = savedHeartsCount > 0 && <span className={"absolute text-[0.5em] text-center text-black rounded-full bg-[#00EAFD]  \n                  h-[1.5em] w-[2.25em] -right-[1.25em] top-[2.25em] \n                  lg:h-[1.5em] lg:w-[2.25em] lg:-right-[1.25em] lg:top-[2.25em] "}>{savedHeartsCount}</span>;
    $[70] = savedHeartsCount;
    $[71] = t41;
  } else {
    t41 = $[71];
  }
  let t42;
  if ($[72] !== t41) {
    t42 = <div className="relative block"><Link aria-label="Saved hearts" className="p-2 rounded focus-visible:ring-2 focus-visible:ring-white cursor-pointer" href="/wishlist">{t40}{t41}</Link></div>;
    $[72] = t41;
    $[73] = t42;
  } else {
    t42 = $[73];
  }
  let t43;
  if ($[74] !== isAuthed) {
    t43 = () => {
      if (isAuthed && window.innerWidth > 1024) {
        setOpenAccountMenu(true);
      }
    };
    $[74] = isAuthed;
    $[75] = t43;
  } else {
    t43 = $[75];
  }
  let t44;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = () => {
      if (window.innerWidth > 1024) {
        setOpenAccountMenu(false);
      }
    };
    $[76] = t44;
  } else {
    t44 = $[76];
  }
  const t45 = isAuthed && openAccountMenu;
  let t46;
  if ($[77] !== basePanel || $[78] !== isAuthed || $[79] !== myHeartsItem?.children) {
    t46 = () => {
      if (!isAuthed) {
        openLoginModal();
      } else {
        if (window && window.innerWidth <= 1024) {
          setStack([basePanel[0], {
            title: "My Hearts",
            items: myHeartsItem?.children || [],
            openerLabel: "My Hearts"
          }]);
          setOpen(true);
          requestAnimationFrame(() => setShow(true));
        }
      }
    };
    $[77] = basePanel;
    $[78] = isAuthed;
    $[79] = myHeartsItem?.children;
    $[80] = t46;
  } else {
    t46 = $[80];
  }
  let t47;
  if ($[81] !== isAuthed) {
    t47 = e_2 => {
      if (e_2.key === "Escape" && isAuthed) {
        setOpenAccountMenu(false);
      }
    };
    $[81] = isAuthed;
    $[82] = t47;
  } else {
    t47 = $[82];
  }
  let t48;
  if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = Icon.person();
    $[83] = t48;
  } else {
    t48 = $[83];
  }
  let t49;
  if ($[84] !== t45 || $[85] !== t46 || $[86] !== t47) {
    t49 = <button type="button" className="p-2 rounded focus-visible:ring-2 focus-visible:ring-white cursor-pointer" aria-label="Account menu" aria-haspopup="menu" aria-expanded={t45} onClick={t46} onKeyDown={t47}>{t48}</button>;
    $[84] = t45;
    $[85] = t46;
    $[86] = t47;
    $[87] = t49;
  } else {
    t49 = $[87];
  }
  let t50;
  if ($[88] !== isAuthed || $[89] !== myHeartsItem || $[90] !== openAccountMenu) {
    t50 = isAuthed && openAccountMenu && myHeartsItem?.children?.length > 0 && <ul role="menu" className={`${Style.subMenuWrapper} absolute right-0 mt-4 xl:w-48 w-40 rounded-xl bg-neutral-900/95 backdrop-blur border border-white/10 shadow-lg z-40`}>{myHeartsItem.children.map(child_3 => <li key={child_3.label} role="none">{child_3.requiresAuth ? <button type="button" role="menuitem" className={`${Style.subMenu} block w-full text-left px-3 py-2 text-sm cursor-pointer focus-visible:ring-2 focus-visible:ring-white`} onClick={() => {
          setOpenAccountMenu(false);
          openLoginWithRedirect(child_3.href);
        }}>{child_3.label}</button> : <Link role="menuitem" href={child_3.href} className={`${Style.subMenu} block px-3 py-2 text-sm cursor-pointer focus-visible:ring-2 focus-visible:ring-white`} onClick={() => setOpenAccountMenu(false)}>{child_3.label}</Link>}</li>)}</ul>;
    $[88] = isAuthed;
    $[89] = myHeartsItem;
    $[90] = openAccountMenu;
    $[91] = t50;
  } else {
    t50 = $[91];
  }
  let t51;
  if ($[92] !== t50) {
    t51 = <div className="hidden lg:block">{t50}</div>;
    $[92] = t50;
    $[93] = t51;
  } else {
    t51 = $[93];
  }
  let t52;
  if ($[94] !== t43 || $[95] !== t49 || $[96] !== t51) {
    t52 = <div ref={accountMenuRef} className="relative flex items-center" onMouseEnter={t43} onMouseLeave={t44}>{t49}{t51}</div>;
    $[94] = t43;
    $[95] = t49;
    $[96] = t51;
    $[97] = t52;
  } else {
    t52 = $[97];
  }
  let t53;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = Icon.hamburger();
    $[98] = t53;
  } else {
    t53 = $[98];
  }
  let t54;
  if ($[99] !== open || $[100] !== openDrawer) {
    t54 = <button ref={desktopBtnRef} className="p-2 -mr-2 rounded focus-visible:ring-2 focus-visible:ring-white cursor-pointer lg:block hidden" aria-label="Open menu" aria-haspopup="dialog" aria-expanded={open} aria-controls="header-mobile-menu" onClick={openDrawer}>{t53}</button>;
    $[99] = open;
    $[100] = openDrawer;
    $[101] = t54;
  } else {
    t54 = $[101];
  }
  let t55;
  if ($[102] !== t42 || $[103] !== t52 || $[104] !== t54) {
    t55 = <div className="flex items-center lg:gap-3 gap-1">{t42}{t52}{t54}</div>;
    $[102] = t42;
    $[103] = t52;
    $[104] = t54;
    $[105] = t55;
  } else {
    t55 = $[105];
  }
  let t56;
  if ($[106] !== t37 || $[107] !== t39 || $[108] !== t55) {
    t56 = <div ref={desktopBarRef} className="container-wrap 2xl:h-17 lg:h-15 h-13 flex items-center justify-between gap-3">{t37}{t39}{t55}</div>;
    $[106] = t37;
    $[107] = t39;
    $[108] = t55;
    $[109] = t56;
  } else {
    t56 = $[109];
  }
  let t57;
  if ($[110] !== currentIndex || $[111] !== dynamicPages || $[112] !== onPanelKeyDown || $[113] !== open || $[114] !== requiresAuthForHref || $[115] !== show || $[116] !== stack || $[117] !== stackForRender) {
    t57 = open && <div role="dialog" id="header-mobile-menu" aria-modal="true" aria-labelledby="header-mobile-menu-title" className="fixed inset-0 z-50"><div onClick={closeDrawer} className={["absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-200", "motion-reduce:transition-none", show ? "opacity-100" : "opacity-0"].join(" ")} /><div ref={panelRef} tabIndex={-1} onKeyDown={onPanelKeyDown} className={["absolute top-0 h-full 2xl:w-[945px] xl:w-[45vw] lg:w-[50vw] md:w-[45vw] w-[85vw] bg-black text-white shadow-xl overscroll-contain lg:rounded-[20px_0_0_20px] rounded-tr-[20px]", "lg:right-0 left-0 right-auto lg:left-auto", "transition-transform duration-300 ease-out", "motion-reduce:transition-none", show ? "translate-x-0" : "-translate-x-full lg:translate-x-full", "flex flex-col"].join(" ")}><div className="h-14 px-4 flex lg:mt-4 mt-3 2xl:pl-18 xl:pl-12 lg:pl-9 pl-7 items-center justify-between">{currentIndex > 0 ? <button className="flex items-center gap-2.5 xl:text-base md:text-sm text-xs font-medium cursor-pointer" onClick={() => {
            popPanel();
            requestAnimationFrame(() => focusFirstMenuItem());
          }}><Image src={Arrow} alt="arrow" width={24} height={24} className="xl:w-3 xl:h-3 w-2.5 h-2.5 rotate-180" /><span>Back</span></button> : <Link href="/" className="flex items-center gap-2 text-sm focus-visible:ring-2 focus-visible:ring-white" onClick={closeDrawer}><Image src={POHIcon} alt="POH icon" width={40} height={40} className="xl:w-[60%] xl:pt-6 lg:w-1/2 lg:pt-4 h-auto w-8" /><span id="header-mobile-menu-title" className="sr-only">Menu</span></Link>}<button aria-label="Close" className="flex items-center justify-center cursor-pointer p-0 2xl:w-12 2xl:h-12 xl:w-10 xl:h-10 lg:w-10 lg:h-10 w-7 h-7 rounded-full bg-[#303030] hover:bg-[#303030c7] transition-opacity duration-300 ease-in-out focus-visible:ring-2 focus-visible:ring-white" onClick={closeDrawer}><CloseRoundIcon className="2xl:w-6 2xl:h-6 xl:w-5 xl:h-5 w-4.5 h-4.5" /><span className="sr-only">close</span></button></div><nav ref={navRef} className="2xl:pl-15 xl:pl-10 lg:pl-8 pl-5 pr-3 2xl:pt-25 xl:pt-20 lg:pt-16 md:pt-12 pt-9 pb-6 overflow-auto scrollbar-none" aria-label="Mobile">{stackForRender.map((panel_0, i_1) => {
            const visible = i_1 === currentIndex;
            const next = stack[i_1 + 1];
            return <section key={`panel-${i_1}-${panel_0.title}`} aria-hidden={!visible} className={!visible ? "hidden" : ""}><ul className={`lg:space-y-2 space-y-0.5 ${Style.sidebarWrapper}`}>{panel_0.items.filter(_temp12).map((item_6, idx_1) => {
                  const isOpenerForNext = !!(next && next.openerLabel === item_6.label);
                  if (item_6.isLoginAction) {
                    return <li key={`${item_6.label}-${idx_1}`}><button className={`${Style.sidebarMenu} lg:font-bold font-semibold cursor-pointer w-full flex items-center justify-between px-3 lg:py-3 py-2 rounded-md text-[15px] focus-visible:ring-2 focus-visible:ring-white`} onClick={openLoginFromDrawer}><div className={`${Style.sideMenu} 2xl:text-4xl xl:text-3xl lg:text-2xl md:text-xl text-lg flex items-center 2xl:gap-7 xl:gap-6 lg:gap-5 gap-4 transition-opacity duration-300 ease-in-out`}><span className="2xl:w-10 xl:w-8 lg:w-6 w-5 flex justify-center">{item_6.icon}</span>{item_6.label}</div></button></li>;
                  }
                  if (item_6.isLogoutAction) {
                    return <li key={`${item_6.label}-${idx_1}`}><button className={`${Style.sidebarMenu} lg:font-bold font-semibold cursor-pointer w-full flex items-center justify-between px-3 lg:py-3 py-2 rounded-md text-[15px] focus-visible:ring-2 focus-visible:ring-white`} onClick={confirmLogoutFromDrawer}><div className={`${Style.sideMenu} 2xl:text-4xl xl:text-3xl lg:text-2xl md:text-xl text-lg flex items-center 2xl:gap-7 xl:gap-6 lg:gap-5 gap-4 transition-opacity duration-300 ease-in-out`}><span className="2xl:w-10 xl:w-8 lg:w-6 w-5 flex justify-center">{item_6.icon}</span>{item_6.label}</div></button></li>;
                  }
                  if (item_6.children || item_6.mobileChildren) {
                    return <li key={`${item_6.label}-${idx_1}`}><button className={`${Style.sidebarMenu} lg:font-bold font-semibold cursor-pointer w-full flex items-center justify-between px-3 lg:py-3 py-2 rounded-md text-[15px] focus-visible:ring-2 focus-visible:ring-white`} onClick={() => setStack(s_1 => [...s_1, {
                        title: item_6.label,
                        items: item_6.mobileChildren ? [...item_6.children, ...item_6.mobileChildren] : item_6.children,
                        openerLabel: item_6.label
                      }])} aria-haspopup="menu" aria-expanded={isOpenerForNext}><div className={`${Style.sideMenu} 2xl:text-4xl xl:text-3xl lg:text-2xl md:text-xl text-lg flex items-center 2xl:gap-7 xl:gap-6 lg:gap-5 gap-4 transition-opacity duration-300 ease-in-out`}><span className="2xl:w-10 xl:w-8 lg:w-6 w-5 flex justify-center">{item_6.icon}</span>{item_6.label}</div><Image src={Arrow} alt="arrow" width={24} height={24} className="2xl:w-6 2xl:h-6 lg:w-5 lg:h-5 w-4 h-4" /></button></li>;
                  }
                  return <li key={`${item_6.label}-${idx_1}`}>{(() => {
                      const needsAuth = requiresAuthForHref(item_6.href);
                      return <Row href={item_6.href} icon={item_6.icon} external={item_6.external} onClick={needsAuth ? () => openLoginFromDrawerWithRedirect(item_6.href) : () => closeDrawer({
                        restoreFocus: false
                      })} requiresAuth={needsAuth} isChild={currentIndex > 0}>{item_6.label}</Row>;
                    })()}</li>;
                })}</ul></section>;
          })}{dynamicPages?.length > 0 && <section aria-hidden="false" className={currentIndex > 0 ? "hidden" : ""}><ul className={`lg:space-y-2 space-y-0.5 ${Style.sidebarWrapper}`}>{dynamicPages.map((page, index_0) => <li key={`${page.label}-${index_0}`}><Row href={page.href} onClick={() => closeDrawer({
                  restoreFocus: false
                })}>{page.label}</Row></li>)}</ul></section>}</nav></div></div>;
    $[110] = currentIndex;
    $[111] = dynamicPages;
    $[112] = onPanelKeyDown;
    $[113] = open;
    $[114] = requiresAuthForHref;
    $[115] = show;
    $[116] = stack;
    $[117] = stackForRender;
    $[118] = t57;
  } else {
    t57 = $[118];
  }
  let t58;
  if ($[119] !== showLoginModal) {
    t58 = showLoginModal && <LoginModal onClose={() => setShowLoginModal(false)} />;
    $[119] = showLoginModal;
    $[120] = t58;
  } else {
    t58 = $[120];
  }
  let t59;
  if ($[121] !== showLogoutModal) {
    t59 = showLogoutModal && <LogoutModal isOpen={showLogoutModal} onConfirm={confirmLogout} onCancel={() => setShowLogoutModal(false)} />;
    $[121] = showLogoutModal;
    $[122] = t59;
  } else {
    t59 = $[122];
  }
  let t60;
  if ($[123] !== t56 || $[124] !== t57 || $[125] !== t58 || $[126] !== t59) {
    t60 = <header className="bg-black text-white sticky top-0 z-50" role="banner">{t56}{t57}{t58}{t59}</header>;
    $[123] = t56;
    $[124] = t57;
    $[125] = t58;
    $[126] = t59;
    $[127] = t60;
  } else {
    t60 = $[127];
  }
  return t60;
}

/* ── Row helper ───────────────────────────────────── */
function _temp12(item_5) {
  return !item_5.hide;
}
function _temp11(item_4) {
  return !item_4.hide;
}
function _temp10(s_0) {
  return s_0.length > 1 ? s_0.slice(0, -1) : s_0;
}
function _temp9(s) {
  return s.length > 1 ? s.slice(0, -1) : s;
}
function _temp8() {
  document.documentElement.style.overflow = "";
}
function _temp7(item_2) {
  return item_2.label === "My Hearts";
}
function _temp6(item_1) {
  return !item_1.isLoginAction && !item_1.isLogoutAction;
}
function _temp5(child_0) {
  return child_0.href;
}
function _temp4(child) {
  return child.label;
}
function _temp3(item) {
  return item.label === "Login";
}
function _temp2(n) {
  return renderIcon(n);
}
function _temp() {
  return 0;
}
function Row(t0) {
  const $ = _c(20);
  const {
    href,
    children,
    icon,
    external,
    onClick,
    isChild,
    requiresAuth
  } = t0;
  const base = `w-full flex items-center justify-between px-3 lg:py-3 py-2 rounded-md text-[15px] cursor-pointer focus-visible:ring-2 focus-visible:ring-white ${Style.sidebarMenu}`;
  const t1 = `${Style.sideMenu} ${isChild ? "font-medium 2xl:text-[28px] xl:text-2xl lg:text-xl md:text-lg text-base" : "lg:font-bold font-semibold 2xl:text-4xl xl:text-3xl lg:text-2xl md:text-xl text-lg"}  flex items-center 2xl:gap-7 xl:gap-6 lg:gap-5 gap-4 transition-opacity duration-300 ease-in-out`;
  const t2 = `${isChild ? "hidden" : "2xl:w-10 xl:w-8 lg:w-6 w-5 flex justify-center"}`;
  let t3;
  if ($[0] !== icon || $[1] !== t2) {
    t3 = <span className={t2}>{icon}</span>;
    $[0] = icon;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== children) {
    t4 = <span>{children}</span>;
    $[3] = children;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t1 || $[6] !== t3 || $[7] !== t4) {
    t5 = <div className={t1}>{t3}{t4}</div>;
    $[5] = t1;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const inner = t5;
  if (requiresAuth) {
    let t6;
    if ($[9] !== inner || $[10] !== onClick) {
      t6 = <button type="button" className={base} onClick={onClick}>{inner}</button>;
      $[9] = inner;
      $[10] = onClick;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    return t6;
  }
  if (external) {
    let t6;
    if ($[12] !== href || $[13] !== inner || $[14] !== onClick) {
      t6 = <a href={href} target="_blank" rel="noopener noreferrer" className={base} onClick={onClick}>{inner}</a>;
      $[12] = href;
      $[13] = inner;
      $[14] = onClick;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    return t6;
  }
  let t6;
  if ($[16] !== href || $[17] !== inner || $[18] !== onClick) {
    t6 = <Link href={href} className={base} onClick={onClick}>{inner}</Link>;
    $[16] = href;
    $[17] = inner;
    $[18] = onClick;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
}